import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Plus, Droplet, Cpu, Power, Plug } from "lucide-react";
import { api, type Device } from "~/lib/api";
import { useAllLive, isOnline, type DeviceLive } from "~/lib/live";
import { useMe } from "~/lib/auth";
import { useRelativeTime } from "~/lib/time";
import { Mark } from "~/components/Brand/Mark";
import { AppShell } from "~/components/AppShell";
import { Button } from "~/components/ui/Button";
import { Skeleton } from "~/components/ui/Skeleton";

export const Route = createFileRoute("/_authed/")({
  component: HomePage,
});

type StatusFilter = "all" | "claimed" | "unclaimed";

function HomePage() {
  const { data: me } = useMe();
  const { data: devices, isLoading } = useQuery({
    queryKey: ["devices"],
    queryFn: () => api.listDevices(),
  });

  const live = useAllLive();
  const isAdminOrSupport = me?.role === "admin" || me?.role === "support";
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const visibleDevices = useMemo(() => {
    if (!devices) return [];
    if (!isAdminOrSupport || statusFilter === "all") return devices;
    return devices.filter((d) =>
      statusFilter === "claimed" ? d.owner_id != null : d.owner_id == null,
    );
  }, [devices, isAdminOrSupport, statusFilter]);

  const total = devices?.length ?? 0;
  const online = useMemo(
    () => (devices ?? []).filter((d) => isOnline(live[d.device_id])).length,
    [devices, live],
  );

  return (
    <AppShell>
      <div className="pt-4 sm:pt-6 lg:pt-8 pb-2">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-largeTitle" style={{ color: "var(--label-primary)" }}>
              Devices
            </h1>
            <div className="text-subhead mt-1" style={{ color: "var(--label-secondary)" }}>
              {total === 0 ? "No devices yet." : `${online} of ${total} online`}
            </div>
          </div>
          <Link to="/claim" className="hidden sm:inline-flex">
            <Button size="md" leadingIcon={<Plus size={16} strokeWidth={2.25} />}>
              Add Device
            </Button>
          </Link>
        </div>

        {isAdminOrSupport && total > 0 && (
          <div className="mt-4">
            <StatusFilterPills
              value={statusFilter}
              onChange={setStatusFilter}
              counts={{
                all: total,
                claimed: devices!.filter((d) => d.owner_id != null).length,
                unclaimed: devices!.filter((d) => d.owner_id == null).length,
              }}
            />
          </div>
        )}
      </div>

      {isLoading && <DeviceGridSkeleton />}

      {!isLoading && total === 0 && <EmptyHome />}

      {!isLoading && total > 0 && (
        <ul
          className="grid gap-3 sm:gap-4 mt-4"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))" }}
        >
          {visibleDevices.map((d) => (
            <li key={d.device_id}>
              <DeviceCard
                device={d}
                live={live[d.device_id]}
                showOwner={isAdminOrSupport}
              />
            </li>
          ))}
          <li>
            <AddDeviceTile />
          </li>
        </ul>
      )}
    </AppShell>
  );
}

function StatusFilterPills({
  value,
  onChange,
  counts,
}: {
  value: StatusFilter;
  onChange: (next: StatusFilter) => void;
  counts: Record<StatusFilter, number>;
}) {
  const options: { key: StatusFilter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "claimed", label: "Claimed" },
    { key: "unclaimed", label: "Unclaimed" },
  ];
  return (
    <div
      role="tablist"
      aria-label="Filter devices by claim status"
      className="inline-flex"
      style={{
        background: "var(--fill-tertiary)",
        border: "0.5px solid var(--separator)",
        borderRadius: 999,
        padding: 3,
      }}
    >
      {options.map((opt) => {
        const active = value === opt.key;
        return (
          <button
            key={opt.key}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.key)}
            className="inline-flex items-center gap-1.5 transition-colors"
            style={{
              padding: "5px 12px",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
              color: active ? "var(--label-primary)" : "var(--label-secondary)",
              background: active ? "var(--bg-grouped-secondary)" : "transparent",
              boxShadow: active ? "var(--shadow-card)" : "none",
            }}
          >
            {opt.label}
            <span
              style={{
                color: active ? "var(--label-tertiary)" : "var(--label-tertiary)",
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {counts[opt.key]}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function DeviceCard({
  device,
  live,
  showOwner,
}: {
  device: Device;
  live: DeviceLive | undefined;
  showOwner: boolean;
}) {
  const online = isOnline(live);
  const seen = useRelativeTime(live?.lastSeenAt ?? device.updated_at);
  const isAquagate = device.device_type === "aquagate";
  const Icon = isAquagate ? Droplet : Cpu;
  const stateLine = online ? "Sending heartbeats" : `Last seen ${seen}`;
  const actuators = isAquagate ? aquagatePreview(live?.actuators) : null;
  const ownerLine = showOwner
    ? device.owner_email ?? (device.owner_id == null ? "Unclaimed" : null)
    : null;

  return (
    <Link
      to="/$deviceId"
      params={{ deviceId: device.device_id }}
      className="group block transition-all active:scale-[0.985]"
      style={{
        background: "var(--bg-grouped-secondary)",
        border: "0.5px solid var(--separator)",
        borderRadius: "var(--radius-group)",
        padding: 18,
        position: "relative",
        overflow: "hidden",
        minHeight: 196,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none"
        style={{
          position: "absolute",
          inset: 0,
          background: online
            ? `radial-gradient(120% 80% at 95% -10%, color-mix(in oklab, var(--system-blue) 18%, transparent), transparent 55%)`
            : "transparent",
          transition: "opacity 320ms var(--ease-soft)",
        }}
      />

      <div className="relative flex items-start justify-between gap-3">
        <span
          aria-hidden
          className="grid place-items-center shrink-0"
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: online ? "var(--system-blue)" : "var(--fill-secondary)",
            color: online ? "#fff" : "var(--label-secondary)",
            transition: "background 320ms var(--ease-soft), color 320ms var(--ease-soft)",
          }}
        >
          <Icon size={20} strokeWidth={2} />
        </span>
        <span
          className="inline-flex items-center gap-1.5"
          style={{
            padding: "3px 9px",
            borderRadius: 999,
            background: online
              ? "color-mix(in oklab, var(--system-green) 16%, transparent)"
              : "var(--fill-tertiary)",
            color: online ? "var(--system-green)" : "var(--label-secondary)",
            fontWeight: 600,
            fontSize: 11,
            letterSpacing: "0.02em",
          }}
        >
          <span
            aria-hidden
            style={{
              width: 6,
              height: 6,
              borderRadius: 999,
              background: online ? "var(--system-green)" : "var(--gray-3)",
              animation: online ? "pulse 1.6s ease-in-out infinite" : undefined,
            }}
          />
          {online ? "Online" : "Offline"}
        </span>
      </div>

      <div className="relative mt-3 min-w-0">
        <div className="text-title-3 truncate" style={{ color: "var(--label-primary)" }}>
          {device.name?.trim() || prettyId(device.device_id)}
        </div>
        <div className="text-footnote mt-0.5 truncate" style={{ color: "var(--label-secondary)" }}>
          {stateLine}
        </div>
      </div>

      <div className="relative flex-1 mt-3">
        {actuators && actuators.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {actuators.map((a) => (
              <ActuatorChip key={a.pin} pin={a.pin} on={a.on} />
            ))}
          </div>
        ) : null}
      </div>

      <div
        className="relative"
        style={{
          paddingTop: 10,
          marginTop: 12,
          borderTop: "0.5px solid var(--separator)",
        }}
      >
        <div
          className="text-caption-1 truncate"
          style={{ color: "var(--label-tertiary)" }}
        >
          {isAquagate ? "Aquagate · 2 valves + AC switch" : capitalize(device.device_type) || "Generic device"}
        </div>
        {ownerLine && (
          <div
            className="text-caption-1 truncate mt-0.5"
            style={{
              color: device.owner_id == null ? "var(--system-orange)" : "var(--label-secondary)",
            }}
            title={ownerLine}
          >
            {device.owner_id == null ? "Unclaimed" : `Owned by ${ownerLine}`}
          </div>
        )}
      </div>
    </Link>
  );
}

function ActuatorChip({ pin, on }: { pin: number; on: boolean }) {
  const label = pin === 0 ? "Valve A" : pin === 1 ? "Valve B" : pin === 2 ? "AC" : `Out ${pin + 1}`;
  const Icon = pin <= 1 ? Droplet : pin === 2 ? Plug : Power;

  return (
    <span
      className="inline-flex items-center gap-1.5"
      style={{
        padding: "4px 9px 4px 7px",
        borderRadius: 999,
        background: on
          ? "color-mix(in oklab, var(--system-green) 14%, transparent)"
          : "var(--fill-tertiary)",
        color: on ? "var(--system-green)" : "var(--label-secondary)",
        fontWeight: 600,
        fontSize: 11.5,
        lineHeight: 1.1,
      }}
    >
      <Icon size={11} strokeWidth={2.25} />
      {label}
      <span
        style={{
          opacity: 0.7,
          fontWeight: 500,
          marginLeft: 1,
        }}
      >
        · {on ? "On" : "Off"}
      </span>
    </span>
  );
}

function AddDeviceTile() {
  return (
    <Link
      to="/claim"
      className="group block transition-transform active:scale-[0.985]"
      style={{
        minHeight: 196,
        height: "100%",
        borderRadius: "var(--radius-group)",
        border: "1.5px dashed var(--separator)",
        background: "transparent",
      }}
    >
      <div className="h-full flex flex-col items-center justify-center gap-3">
        <span
          aria-hidden
          className="grid place-items-center"
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            background: "var(--fill-tertiary)",
            color: "var(--system-blue)",
          }}
        >
          <Plus size={22} strokeWidth={2.25} />
        </span>
        <span className="text-headline" style={{ color: "var(--system-blue)" }}>
          Add Device
        </span>
      </div>
    </Link>
  );
}

function DeviceGridSkeleton() {
  return (
    <ul
      className="grid gap-3 sm:gap-4 mt-4"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))" }}
    >
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <li key={i}>
          <div
            style={{
              minHeight: 196,
              borderRadius: "var(--radius-group)",
              background: "var(--bg-grouped-secondary)",
              border: "0.5px solid var(--separator)",
              padding: 18,
            }}
            className="flex flex-col"
          >
            <div className="flex justify-between">
              <Skeleton width={40} height={40} rounded={12} />
              <Skeleton width={64} height={20} rounded={999} />
            </div>
            <div className="mt-3 space-y-2">
              <Skeleton width="60%" height={20} rounded={6} />
              <Skeleton width="40%" height={12} rounded={4} />
            </div>
            <div className="mt-3 flex gap-1.5">
              <Skeleton width={70} height={20} rounded={999} />
              <Skeleton width={70} height={20} rounded={999} />
            </div>
            <div
              style={{ paddingTop: 10, marginTop: "auto", borderTop: "0.5px solid var(--separator)" }}
            >
              <Skeleton width="55%" height={12} rounded={4} />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyHome() {
  return (
    <div className="flex-1 flex flex-col justify-center items-center text-center max-w-md mx-auto px-2 py-16 sm:py-24">
      <Mark size={88} style={{ marginBottom: 24 }} />
      <h2 className="text-title-1 mb-2" style={{ color: "var(--label-primary)" }}>
        Welcome
      </h2>
      <p className="text-body mb-8" style={{ color: "var(--label-secondary)" }}>
        Add your first device to get started. Setup takes about a minute.
      </p>
      <Link to="/claim" className="block w-full max-w-xs">
        <Button size="lg" leadingIcon={<Plus size={18} />} className="w-full">
          Add Device
        </Button>
      </Link>
      <p className="mt-5 text-footnote" style={{ color: "var(--label-tertiary)" }}>
        The QR code is on the back of your device.
      </p>
    </div>
  );
}

function aquagatePreview(actuators: { pin: number; on: boolean }[] | undefined) {
  if (!actuators || actuators.length === 0) return null;
  const byPin = new Map(actuators.map((a) => [a.pin, a]));
  const out: { pin: number; on: boolean }[] = [];
  for (const pin of [0, 1, 2]) {
    const a = byPin.get(pin);
    if (a) out.push({ pin, on: a.on });
  }
  return out;
}

function prettyId(deviceId: string) {
  if (!deviceId) return "Device";
  return deviceId.length > 10 ? `Device ${deviceId.slice(-6).toUpperCase()}` : deviceId;
}

function capitalize(s: string | undefined): string {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

